import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Search, ImageIcon, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${BASE}${u}`;
  return u;
};

/**
 * MediaPicker — modal for choosing media from the CMS library.
 * Props:
 *  - open: boolean
 *  - onClose(): close without selection
 *  - onSelect(url): called with the chosen media URL
 *  - kind: 'image' | 'any' — restrict filter (default: 'image')
 */
const MediaPicker = ({ open, onClose, onSelect, kind = "image" }) => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (kind === "image") params.kind = "image";
      const res = await axios.get(`${API}/cms/media`, { params });
      setItems(res.data);
    } catch {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  }, [query, kind]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await axios.post(`${API}/cms/media`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Media Library</h2>
            <p className="text-xs text-slate-500">Pick an image to insert, or upload a new one</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-media-picker"><X size={18} /></button>
        </header>

        <div className="flex items-center gap-2 p-3 border-b border-slate-200 bg-slate-50">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Search media…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40"
              data-testid="media-picker-search"
            />
          </div>
          <label className="ml-auto inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white text-sm font-medium px-3.5 py-1.5 rounded-lg cursor-pointer">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
            <input type="file" multiple accept={kind === "image" ? "image/*" : "*/*"} onChange={onUpload} className="hidden" data-testid="media-picker-upload" disabled={uploading} />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-slate-400 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-slate-400 text-sm text-center py-16">No media found. Try uploading a file.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map((m) => {
                const url = resolveUrl(m.url);
                const isImage = (m.content_type || "").startsWith("image/");
                return (
                  <button
                    key={m.id}
                    onClick={() => { onSelect?.(m.url); onClose?.(); }}
                    className="group relative border border-slate-200 rounded-lg overflow-hidden hover:border-prepify-500 hover:ring-2 hover:ring-prepify-500/30 transition bg-white"
                    data-testid={`media-item-${m.id}`}
                  >
                    <div className="aspect-square bg-slate-50 flex items-center justify-center">
                      {isImage ? (
                        <img src={url} alt={m.alt_text || m.filename} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-slate-400">
                          <FileText size={32} />
                          <span className="text-[10px] uppercase">{(m.content_type || "file").split("/").pop()}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2 text-left">
                      <div className="text-xs font-medium text-slate-700 truncate">{m.filename}</div>
                      <div className="text-[10px] text-slate-400">{m.kind || (isImage ? "image" : "file")}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
